import type { AuditLogEntry } from '@diora/contracts';
import { formatDateTime } from '@diora/contracts';
import { ClipboardList } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { Alert, Badge, Button, EmptyState, Modal, Pagination, SkeletonRows, Toolbar } from '@/components/ui';
import { useAdminList, useListParams } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';

const URL = '/admin/audit-logs';

export function AuditPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = useAuth();

  const [params, setParams] = useListParams();
  const list = useAdminList<AuditLogEntry>(qk.auditLogs(params), URL, params);
  const [inspecting, setInspecting] = useState<AuditLogEntry | null>(null);

  if (!can('OWNER')) {
    return (
      <>
        <PageHeader title={t('audit.title')} />
        <Alert tone="warning">{t('errors.FORBIDDEN')}</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.subtitle')} />

      <Toolbar>
        <p className="dio-hint">{t('audit.redactedNote')}</p>
      </Toolbar>

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<ClipboardList aria-hidden="true" />} title={t('audit.empty')} />
      ) : (
        <div className="dio-table-wrap">
          <table className="dio-table">
            <thead>
              <tr>
                <th scope="col">{t('audit.when')}</th>
                <th scope="col">{t('audit.actor')}</th>
                <th scope="col">{t('audit.action')}</th>
                <th scope="col">{t('audit.entity')}</th>
                <th scope="col">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((row) => (
                <tr key={row.id}>
                  <td className="dio-table__num">{formatDateTime(row.createdAt, locale)}</td>
                  <td>{row.actor ? row.actor.displayName : <Badge tone="outline">{t('audit.system')}</Badge>}</td>
                  <td><code dir="ltr">{row.action}</code></td>
                  <td><code dir="ltr">{row.entityType}</code></td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => setInspecting(row)}>
                      {t('audit.viewChange')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

      {inspecting && (
        <Modal open onClose={() => setInspecting(null)} title={inspecting.action}>
          <div className="flex flex-col gap-4">
            <Alert tone="info">{t('audit.redactedNote')}</Alert>

            <p className="dio-meta" dir="ltr">
              {inspecting.entityType} · {inspecting.entityId ?? '—'}
              {inspecting.requestId ? ` · ${inspecting.requestId}` : ''}
            </p>

            <ChangeBlock title={t('audit.before')} value={inspecting.before} />
            <ChangeBlock title={t('audit.after')} value={inspecting.after} />
          </div>
        </Modal>
      )}
    </>
  );
}

function ChangeBlock({ title, value }: { title: string; value: unknown }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="dio-label">{title}</p>
      {value === null || value === undefined ? (
        <p className="dio-meta">{t('common.none')}</p>
      ) : (
        // Always left-to-right: this is stored data, and mirroring JSON makes
        // it unreadable.
        <pre
          dir="ltr"
          className="dio-input"
          style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', maxBlockSize: '18rem', overflow: 'auto' }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
